import { useState } from "react";
import moment from "moment-hijri";
import { useTranslation } from "react-i18next";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import LeftArrowImg from "../../assets/img/icon/left_arrow.png";
import RightArrowImg from "../../assets/img/icon/right_arrow.png";
import styles from "./dashboard.module.css";

export const OneWeekCalendar = ({ selectedDate, onDateChange }) => {
  const { t, i18n } = useTranslation();
  const dir = i18n.dir();
  const lang = i18n.language;

  const [currentDate, setCurrentDate] = useState(moment());
  const [viewMode, setViewMode] = useState("week"); // "week" | "month"

  const startOfWeek = currentDate.clone().startOf("week"); // Sunday
  const weekDays = Array.from({ length: 7 }, (_, i) =>
    startOfWeek.clone().add(i, "days")
  );

  // Month days (used only in month mode)
  const startOfMonth = currentDate.clone().startOf("month").startOf("week");
  const endOfMonth = currentDate.clone().endOf("month").endOf("week");

  const monthDays = [];
  let dayCursor = startOfMonth.clone();
  while (dayCursor.isBefore(endOfMonth, "day")) {
    monthDays.push(dayCursor.clone());
    dayCursor.add(1, "day");
  }

  const hijriMonths = t("admin_pages.dashboard.calendar.hijri_months", {
    returnObjects: true,
  });
  const gregorianMonths = t("admin_pages.dashboard.calendar.gregorian_months", {
    returnObjects: true,
  });
  const weekDayNames = t("admin_pages.dashboard.calendar.weekdays", {
    returnObjects: true,
  });

  const toggleViewMode = () => {
    setViewMode((prev) => (prev === "week" ? "month" : "week"));
  };

  const handlePrevWeek = () => {
    const newDate = currentDate.clone().subtract(1, viewMode);
    setCurrentDate(newDate);
  };

  const handleNextWeek = () => {
    const newDate = currentDate.clone().add(1, viewMode);
    setCurrentDate(newDate);
  };

  const toLocalizedNumber = (number) => {
    if (lang === "ar") {
      return number.toString().replace(/\d/g, (d) => "٠١٢٣٤٥٦٧٨٩"[d]);
    }
    return number.toString();
  };

  // Get the localized month name based on current language
  const getLocalizedMonth = (date) => {
    if (lang === "ar") {
      return gregorianMonths[date.month()]; // Use the translated month names
    }
    return date.format("MMMM");
  };

  return (
    <div style={{ overflow: "hidden" }}>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <img
          src={dir === "rtl" ? RightArrowImg : LeftArrowImg}
          alt="left arrow"
          className={styles.arrowIcon}
          onClick={handlePrevWeek}
        />
        <span className={styles.calendarHeader}>
          {getLocalizedMonth(currentDate)}{" "}
          {toLocalizedNumber(currentDate.year())}
        </span>
        <img
          src={dir === "rtl" ? LeftArrowImg : RightArrowImg}
          alt="right arrow"
          className={styles.arrowIcon}
          onClick={handleNextWeek}
        />
      </div>

      <div
        className={`${styles.calendarWrapper} ${
          viewMode === "month" ? styles.expand : styles.collapse
        }`}
      >
        {/* Weekday header */}
        <div className={styles.weekHeader}>
          {weekDayNames.map((dayName, index) => (
            <div key={index} className={styles.weekHeaderCell}>
              {dayName}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div
          className={`${styles.calendarRow} ${
            viewMode === "month" ? styles.monthView : styles.weekView
          }`}
        >
          {(viewMode === "week" ? weekDays : monthDays).map((day) => {
            const hijriDay = day.iDate();
            const hijriMonth = day.iMonth();
            const hijriYear = day.iYear();
            const gregorianDay = day.date();

            const isSelected = selectedDate?.isSame(day, "day");

            return (
              <div
                key={day.toString()}
                className={`${styles.calendarTile} ${
                  isSelected ? styles.activeTile : ""
                }`}
                title={`${hijriDay} ${hijriMonths[hijriMonth]} ${hijriYear}`}
                onClick={() => onDateChange(day)}
              >
                <div className={styles.dayNum}>
                  {toLocalizedNumber(gregorianDay)}
                </div>
                <div className={styles.hijriDayMonth}>
                  <span>{toLocalizedNumber(hijriDay)}</span>
                  <span>/</span>
                  <span>{toLocalizedNumber(hijriMonth + 1)}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="text-center">
        <span className={styles.toggleArrow} onClick={toggleViewMode}>
          {viewMode === "week" ? <FaChevronDown /> : <FaChevronUp />}
        </span>
      </div>
    </div>
  );
};
